import {
  Box,
  Container,
  Heading,
  HStack,
  Image,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { Elements } from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/Auth";
import { usePremium } from "../../../contexts/Premium";
import { stripeProducts } from "../../../core/constants";
import RedwingUpgrade from "../../../resources/images/redwing-upgrade.png";
import { paymentIntent } from "../../../services/userApi";
import { getStripePromise } from "../../../services/stripe";
import Spinner from "../../../shared/components/Spinner";
import Benefits from "../components/Benefits";
import { trackEvent } from "../../../shared/analytics";
import CheckoutForm from "../components/CheckoutForm";

const Subscribe = () => {
  const { authData, updateUser } = useAuth();
  const { isPremium, setIsPremium } = usePremium();
  const navigate = useNavigate();
  const toast = useToast();
  const [selectedPlan, setSelectedPlan] = useState("1");
  const { data, isSuccess, isLoading } = useQuery("intent", paymentIntent, {
    refetchOnWindowFocus: false,
    enabled: !isPremium,
  });

  useEffect(() => {
    if (!isPremium) {
      trackEvent("paywall_viewed", { source: "go_premium_page" });
    }
  }, [isPremium]);

  const onSelectPlan = (plan) => {
    setSelectedPlan(plan);
    trackEvent("plan_selected", {
      plan: stripeProducts[plan]?.title,
      price_id: stripeProducts[plan]?.priceId,
      store: "stripe",
    });
  };

  const onSuccess = (userData) => {
    if (!userData.id && !userData.premium) {
      return;
    }
    updateUser(userData);
    setIsPremium(true);
    navigate("/profile");
    toast({
      title: "Welcome to Redwing Premium",
      description: "Everything has been unlocked. Enjoy your new features!",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  // If premium redirect away from here
  if (isPremium) {
    return <Navigate to="/news" />;
  }

  return (
    <Container maxW="container.lg">
      <SimpleGrid mt="6" columns={{ sm: 1, md: 1, lg: 2 }} spacing={2}>
        <Box>
          <Box bg="gray.700" borderRadius="lg" p="6">
            <Text mb="3">Choose your plan:</Text>
            <RadioGroup
              value={selectedPlan}
              defaultValue="1"
              onChange={onSelectPlan}
            >
              <Stack>
                <Box
                  padding="3"
                  borderRadius="lg"
                  borderWidth="3px"
                  borderColor={selectedPlan === "1" ? "red.500" : ""}
                >
                  <Radio value="1">
                    <Text fontSize="1.3em" fontWeight="bold">
                      {stripeProducts[1].price}
                    </Text>
                  </Radio>
                </Box>
                <Box
                  padding="3"
                  borderRadius="lg"
                  borderWidth="3px"
                  borderColor={selectedPlan === "2" ? "red.500" : ""}
                >
                  <Radio value="2">
                    <HStack>
                      <Text fontSize="1.3em" fontWeight="bold">
                        {stripeProducts[2].price}{" "}
                      </Text>
                      <Text color="red.200">(save 16%)</Text>
                    </HStack>
                  </Radio>
                </Box>
              </Stack>
            </RadioGroup>
            <Heading as="h6" mt="6" mb="3" size="md">
              Unlock Everything
            </Heading>
            <Benefits />
            <Text mt="3">Cancel anytime.</Text>
          </Box>
        </Box>
        <Box>
          <Image
            src={RedwingUpgrade}
            alt="Redwing - Upgrade"
            maxHeight="160px"
            w="full"
            objectFit="cover"
            borderTopRadius="lg"
          />
          <Box bg="white" borderBottomRadius="lg" p="6" mb="6">
            {isLoading || !data ? (
              <Spinner />
            ) : (
              <>
                <Heading as="h4" size="sm" color="gray.700" mb="6">
                  Redwing {stripeProducts[selectedPlan].title} Premium -{" "}
                  {stripeProducts[selectedPlan].price}
                </Heading>
                {isSuccess && data.intent.clientSecret && (
                  <Elements
                    stripe={getStripePromise()}
                    options={{
                      clientSecret: data.intent.clientSecret,
                      defaultValues: {
                        billingDetails: {
                          email: authData.email,
                          name: `${authData.firstName} ${authData.lastName}`,
                        },
                      },
                    }}
                  >
                    <CheckoutForm
                      user={authData}
                      priceId={stripeProducts[selectedPlan].priceId}
                      plan={stripeProducts[selectedPlan].title}
                      amount={stripeProducts[selectedPlan].amount}
                      currency={stripeProducts[selectedPlan].currency}
                      onSuccess={onSuccess}
                    />
                  </Elements>
                )}
              </>
            )}
          </Box>
        </Box>
      </SimpleGrid>
    </Container>
  );
};

export default Subscribe;
